/**
 * Cloudflare Turnstile token for the anonymous sign-up / sign-in requests. The API says whether the check is on
 * (GET /v1/identity/bot-check -> siteKey | null); when it is off this resolves to no header and nothing loads.
 * Invisible unless Cloudflare needs an interaction (then the widget shows inside the form).
 */
import { bff } from './bff';

interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  remove(id: string): void;
}
declare global { interface Window { turnstile?: TurnstileApi } }

let ready: Promise<string | null> | null = null;
const setup = (): Promise<string | null> => (ready ??= (async () => {
  const cfg = await bff<{ siteKey: string | null }>('/v1/identity/bot-check').catch(() => null);
  if (!cfg?.siteKey) return null;
  await new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true; s.onload = () => resolve(); s.onerror = () => reject(new Error('turnstile'));
    document.head.appendChild(s);
  }).catch(() => undefined);
  return window.turnstile ? cfg.siteKey : null;
})());

/** Headers to spread into the request: `{ 'cf-turnstile-response': token }`, or `{}` when the check is off/unavailable. */
export async function botCheckHeaders(form: HTMLElement, locale: string): Promise<Record<string, string>> {
  const siteKey = await setup();
  if (!siteKey || !window.turnstile) return {};
  const box = document.createElement('div');
  form.appendChild(box);
  const api = window.turnstile;
  const token = await new Promise<string | null>((resolve) => {
    try {
      api.render(box, { sitekey: siteKey, appearance: 'interaction-only', language: locale === 'ar' ? 'ar' : 'en', callback: (t: string) => resolve(t), 'error-callback': () => resolve(null), 'timeout-callback': () => resolve(null) });
    } catch { resolve(null); }
  });
  box.remove();
  return token ? { 'cf-turnstile-response': token } : {};
}
